import { RadioGroup } from '@mui/material';
import { useState } from 'react';
import { ControlLabel, Form, FormGroup, Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../contexts/ToastContext';
import { LabeledCheckbox, LabeledRadio, TextArea } from '../../../generic';
import Button, { LoaderButton } from '../../../generic/Button';
import { isChurnFormValid } from '../utils';

import { useSubmitChurnForm } from './useSubmitChurnForm';

import styles from './ChurnFormModal.module.less';

type ChurnFormModalProps = {
  isShown: boolean;
  onHide: () => void;
  onFormSubmit: () => void;
};

type OptionType = 'mostValuableFeatures' | 'reasons';

export type ChurnFormData = {
  mostValuableFeatures: Set<string>;
  mostValuableFeaturesNotes: string;
  reasons: Set<string>;
  reasonsNote: string;
  switchTool: boolean;
  switchToolNotes: string;
};

const DEFAULT_FORM_DATA: ChurnFormData = {
  mostValuableFeatures: new Set(),
  mostValuableFeaturesNotes: '',
  reasons: new Set(),
  reasonsNote: '',
  switchTool: false,
  switchToolNotes: '',
};

const REASONS = [
  { label: 'Cost', value: 'cost' },
  { label: 'Ownership of tooling changed', value: 'tool ownership changed' },
  { label: 'Lack of features', value: 'lack of features' },
  { label: 'Technical Issues', value: 'technical issues' },
  { label: 'No longer need the service', value: 'no longer needed' },
  { label: 'Other (Please specify)', value: 'other' },
];

const FEATURES = [
  { label: 'Code Analysis', value: 'code analysis' },
  { label: 'License Compliance', value: 'license compliance' },
  { label: 'Reporting', value: 'reporting' },
  { label: 'SBOM Generation', value: 'sbom generation' },
  { label: 'Integrations', value: 'integrations' },
  { label: 'API & Tooling', value: 'api and tooling' },
  { label: 'Other (Please specify)', value: 'other' },
];

const ChurnFormModal: VoidFunctionComponent<ChurnFormModalProps> = ({
  isShown,
  onHide,
  onFormSubmit,
}) => {
  const {
    isLoading: isSubmittingForm,
    mutateAsync: submitChurnForm,
  } = useSubmitChurnForm();

  const addToast = useAddToast();

  const [data, setData] = useState<ChurnFormData>(DEFAULT_FORM_DATA);

  const onSubmit = async () => {
    try {
      await submitChurnForm({
        ...data,
        mostValuableFeatures: [...data.mostValuableFeatures],
        reasons: [...data.reasons],
      });
      setData(DEFAULT_FORM_DATA);
      onFormSubmit();
    } catch (e: unknown) {
      addToast({ type: 'error', content: `Error while submitting form. Error: ${(e as Error)?.message}` });
    }
  };

  const isOptionSelected = (type: OptionType, option: string) => data[type].has(option);

  const toggleOption = (type: OptionType, option: string) => {
    const options = data[type];

    if (options.has(option)) {
      options.delete(option);
    } else {
      options.add(option);
    }

    setData((currentData) => ({ ...currentData, [type]: options }));
  };

  const onChangeSwitchTool = (newValue: boolean) =>
    setData((currentData) => ({ ...currentData, switchTool: newValue }));
  const onChangeNotes = (newValue: string, notesField: string) =>
    setData((currentData) => ({ ...currentData, [notesField]: newValue }));

  return (
    <Modal show={isShown} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          Before you go, please let us know why
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <FormGroup className="mb-3">
            <ControlLabel>What is your reason for canceling?</ControlLabel>
            {
              REASONS.map((r) => (
                <LabeledCheckbox
                  className={styles.options}
                  isChecked={isOptionSelected('reasons', r.value)}
                  key={`reasons-${r.value}`}
                  label={r.label}
                  onChange={() => toggleOption('reasons', r.value)}
                />
              ))
            }
            <TextArea
              isDisabled={!isOptionSelected('reasons', 'other')}
              label="Type your note..."
              onChange={(newValue) => onChangeNotes(newValue, 'reasonsNote')}
              value={data.reasonsNote}
            />
          </FormGroup>

          <FormGroup className="mb-3">
            <ControlLabel>Are you planning to switch to a different tool?</ControlLabel>
            <RadioGroup
              value={data.switchTool}
              className={styles.radioGroup}
              onChange={(e) => onChangeSwitchTool(e.target.value === 'true')}
            >
              <LabeledRadio label="No" value="false" />
              <LabeledRadio label="Yes (Please specify)" value="true" />
            </RadioGroup>
            <TextArea
              isDisabled={!data.switchTool}
              label="Type your note..."
              onChange={(newValue) => onChangeNotes(newValue, 'switchToolNotes')}
              value={data.switchToolNotes}
            />
          </FormGroup>

          <FormGroup className="mb-3">
            <ControlLabel>Which features of FOSSA did you find most valuable?</ControlLabel>
            {
              FEATURES.map((r) => (
                <LabeledCheckbox
                  className={styles.options}
                  isChecked={isOptionSelected('mostValuableFeatures', r.value)}
                  key={`mostValuableFeatures-${r.value}`}
                  label={r.label}
                  onChange={() => toggleOption('mostValuableFeatures', r.value)}
                />
              ))
            }
            <TextArea
              isDisabled={!isOptionSelected('mostValuableFeatures', 'other')}
              label="Type your note..."
              onChange={(newValue) => onChangeNotes(newValue, 'mostValuableFeaturesNotes')}
              value={data.mostValuableFeaturesNotes}
            />
          </FormGroup>

          <FormGroup className={styles.actionButtons}>
            <Button size="medium" type="secondary" onClick={() => onHide()}>Keep the Business Plan</Button>
            <LoaderButton
              isDisabled={!isChurnFormValid(data)}
              isLoading={isSubmittingForm}
              onClick={onSubmit}
              size="medium"
              type="error"
            >
              Downgrade Plan
            </LoaderButton>
          </FormGroup>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChurnFormModal;
